import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, useContentToken } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useInternalizeLinks } from '../hooks/useInternalizeLinks';

interface PageBlurbProps {
  /** Property field name for the blurb (e.g. 'property_floorplans_blurb') */
  blurbField?: string;
  /** Unique prefix so each page's blurb token is independently editable */
  tokenPrefix?: string;
  /** Static fallback text when no property blurb is available */
  defaultText?: string;
  /** Tailwind max-width class for the content column. Defaults per page. */
  maxWidthClass?: string;
}

// Longer, section-headed blurbs (e.g. the Neighborhood page) read better in a
// wider column than the default. Keyed by tokenPrefix so it stays per-page.
const BLURB_MAX_WIDTH_BY_PREFIX: Record<string, string> = {
  'neighborhood-blurb': 'max-w-6xl',
};
const DEFAULT_BLURB_MAX_WIDTH = 'max-w-3xl';

/**
 * Normalizes pasted rich-text formatting so every inner-page blurb renders in
 * the theme's default body style — matching the Pet Policy page. Pasting text
 * (e.g. to preserve hyperlinks) carries the source's inline font/size/color/
 * alignment on the inner nodes, which visually overrides the wrapper's theme
 * classes. These rules force those inner nodes back to `inherit`, while leaving
 * <a> to the theme's own link styling so hyperlinks keep working and look
 * consistent. Bold/italic are preserved (font-weight/font-style are untouched).
 */
const BLURB_NORMALIZE_CSS =
  '[data-vibesite-slot="PageBlurb"] [data-vibesite-richtext] :where(p,span,div,font,ul,ol,li,strong,em,b,i,br){' +
  'font-family:inherit!important;font-size:inherit!important;line-height:inherit!important;' +
  'letter-spacing:inherit!important;color:inherit!important;background-color:transparent!important;' +
  'text-align:inherit!important}' +
  '[data-vibesite-slot="PageBlurb"] [data-vibesite-richtext] a{' +
  'color:var(--vibe-color-hyperlink,var(--vibe-color-primary))!important;' +
  'font-family:inherit!important;font-size:inherit!important;background-color:transparent!important}';

export function PageBlurb({ blurbField, tokenPrefix, defaultText, maxWidthClass }: PageBlurbProps) {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const prefix = tokenPrefix || 'pageblurb';
  const textPath = `${prefix}.text`;
  const widthClass =
    maxWidthClass || BLURB_MAX_WIDTH_BY_PREFIX[prefix] || DEFAULT_BLURB_MAX_WIDTH;

  // Keep in-copy links navigating within the site rather than out to the
  // absolute canonical URLs stored in the blurb.
  const text = useContentToken(textPath, { defaultField: blurbField, defaultValue: defaultText || '' });
  useInternalizeLinks(sectionRef, [text]);

  return (
    <section ref={sectionRef as React.RefObject<HTMLElement>} className="reveal-fade-up pt-10 pb-4 md:pt-14 md:pb-6 bg-[var(--vibe-color-background)]">
      <style dangerouslySetInnerHTML={{ __html: BLURB_NORMALIZE_CSS }} />
      <div className={`${widthClass} mx-auto px-4 sm:px-6 lg:px-8 text-center`}>
        <RichTextContent
          path={textPath}
          defaultField={blurbField}
          defaultValue={defaultText || ''}
          as="p"
          className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-muted)]"
        />
      </div>

      {/* Schema-lint anchor for static analysis — hidden at runtime */}
      <RichTextContent
        path="pageblurb.text"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageBlurb',
  displayName: 'Page Blurb',
  tokens: {
    'pageblurb.text': {
      label: 'Blurb Text',
      type: 'richtext',
      defaultValue: '<span data-type="field-token" data-field="property_long_description"></span>',
      suggestedFields: [
        'property_floorplans_blurb',
        'property_amenities_blurb',
        'property_gallery_blurb',
        'property_neighborhood_blurb',
        'property_virtualtours_blurb',
        'property_contactus_blurb',
        'property_reviews_blurb',
        'property_pets_blurb',
        'property_applynow_blurb',
        'property_residentaccess_blurb',
        'property_scheduletour_blurb',
        'property_interactivemap_blurb',
      ],
    },
  },
};

(PageBlurb as any).contentSchema = contentSchema;

export default PageBlurb;
